import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, catchError, defer, distinctUntilChanged, filter, first, forkJoin, from, map, Observable, of, ReplaySubject, shareReplay, switchMap, tap } from 'rxjs';
import { TradeKey } from '../../core/model/api';
import { TradeKeyWithPermissions } from '../../core/model/common';
import { RestClientFactory } from '../../core/market/rest-client-factory';
import { AuthenticatorService } from '../../core/service/authenticator.service';
import { StoredStateHelper } from '../../core/service/stored-state.service';
import { cloneDeep } from 'lodash-es';
import { BalanceService } from '../balance/balance.service';
import { MarketError } from 'src/app/core/error/codes';
import { IdSystemUserService } from 'src/app/core/service/id-system';

@Injectable({
  providedIn: 'root'
})
export class KeysService implements OnDestroy {

  private authenticator = inject(AuthenticatorService)
  private idSystem = inject(IdSystemUserService)
  private rcf = inject(RestClientFactory)
  private stateHelper = inject(StoredStateHelper)
  private balanceService = inject(BalanceService)
  private activeKeySubject = new ReplaySubject<TradeKeyWithPermissions>(1)
  private loadingSubject = new BehaviorSubject(false)

  private demoKeys: TradeKeyWithPermissions[] = [{
    id: -1,
    name: 'Demo',
    market: 'bybit',
    created: 0,
    authData: {
      publicKey: '',
      secretKey: '',
    },
    canRead: false,
    canTrade: false,
    isDemo: true,
  }, {
    id: -2,
    name: 'Demo',
    market: 'binance',
    created: 0,
    authData: {
      publicKey: '',
      secretKey: '',
    },
    canRead: false,
    canTrade: false,
    isDemo: true,
  }, {
    id: -3,
    name: 'Demo',
    market: 'okx',
    created: 0,
    authData: {
      publicKey: '',
      secretKey: '',
      password: '',
    },
    canRead: false,
    canTrade: false,
    isDemo: true,
  }]

  private keysRequest$ = defer(() => {
    this.loadingSubject.next(true)  // включаем флаг загрузки
    return this.idSystem.getApiKeys().pipe(
      switchMap(keys => keys.length ? forkJoin(keys.map(key => this.fetchKeyPermissions(key))) : of([]))
    )
  })

  readonly keys$ = this.authenticator.auth$.pipe(
    switchMap(flag => flag ? this.keysRequest$ : of([])),
    map(keys => this.appendDemoKeys(keys)),
    switchMap(keys => {
      if (this.authenticator.isAuthed) {
        // Если авторизован, то пытаемся получить активный ключ из состояния
        return this.stateHelper.getTerminalActiveKey().pipe(
          switchMap(id => {
            const activeKey = keys.find(k => k.id == id)
            // Активный ключ найден в состоянии
            if (activeKey) {
              return of(activeKey)
            }
            // Разруливаем активный ключ на основании баланса
            return this.resolveActiveKey(keys).pipe(
              tap(key => {
                if (!key.isDemo) {
                  this.stateHelper.setTerminalActiveKey(key.id)
                }
              })
            )
          }),
          tap(activeKey => this.activeKeySubject.next(activeKey)),
          map(() => keys)
        )
      }
      this.activeKeySubject.next(keys[0])
      return of(keys)
    }),
    tap(() => this.loadingSubject.next(false)),  // выключаем флаг загрузки
    shareReplay(1)
  )

  readonly activeKey$ = this.activeKeySubject.pipe(
    distinctUntilChanged()
  )

  readonly loading$ = this.loadingSubject.pipe(
    distinctUntilChanged()
  )

  ngOnDestroy() {
    this.activeKeySubject.complete()
    this.loadingSubject.complete()
  }

  getActiveKey(onlyReal = true) {
    return this.activeKey$.pipe(
      first(),
      filter(key => onlyReal ? !key.isDemo : true),
    )
  }

  triggerActiveKey(key: TradeKeyWithPermissions) {
    this.stateHelper.setTerminalActiveKey(key.id)
    this.activeKeySubject.next(key)
  }

  /**
   * Устанавливает разрешения для ключа
   */
  private fetchKeyPermissions(key: TradeKey): Observable<TradeKeyWithPermissions> {
    const rest = this.rcf.createForKey(key)

    if (rest.isBybit) {
      return from(rest.bybit.getQueryApiKey()).pipe(
        map(data => {
          const key2: TradeKeyWithPermissions = {
            ...key,
            canRead: true,
            canTrade: data.result.readOnly == 0,
          }
          if (data.result.ips) {
            if (data.result.ips.length == 1 && data.result.ips[0] == '*') {
              key2.ips = []
            } else {
              key2.ips = data.result.ips
            }
          }
          return key2
        }),
        catchError(() => {
          const key2: TradeKeyWithPermissions = {
            ...key,
            canRead: false,
            canTrade: false,
          }
          return of(key2)
        })
      )
    }

    if (rest.isBinanceUsdm) {
      return from(rest.binanceUsdm.testOrder({
        symbol: 'BTCUSDT',
        side: 'BUY',
        type: 'MARKET',
        quantity: 0.01,
      })).pipe(
        map(() => {
          const key2: TradeKeyWithPermissions = {
            ...key,
            canRead: true,
            canTrade: true,
          }
          return key2
        }),
        catchError(() => {
          const key2: TradeKeyWithPermissions = {
            ...key,
            canRead: true,
            canTrade: false,
          }
          return of(key2)
        })
      )
    }

    if (rest.isOkx) {
      return from(rest.okx.cancelOrder({
        instId: 'BTC-USDT-SWAP',
        ordId: '0',
      })).pipe(
        map(() => {
          const key2: TradeKeyWithPermissions = {
            ...key,
            canRead: true,
            canTrade: true,
          }
          return key2
        }),
        catchError((error: any) => {
          if (error instanceof MarketError && error.apiCode == '50120') {
            // Нет разрешения на торговлю
            return of({
              ...key,
              canRead: true,
              canTrade: false,
            })
          }
          if (error instanceof MarketError && error.apiCode == '50110') {
            // IP не в белом списке
            return of({
              ...key,
              canRead: false,
              canTrade: false,
            })
          }
          if (error instanceof MarketError && error.apiDetails && error.apiDetails[0] && error.apiDetails[0].sCode) {
            // Если ошибка от системы обработки ордеров, то разрешение на торговлю есть
            return of({
              ...key,
              canRead: true,
              canTrade: true,
            })
          }
          return of({
            ...key,
            canRead: false,
            canTrade: false,
          })
        })
      )
    }

    throw new Error(`Unknown ${key.market}`)
  }

  /**
   * Выбирает активный ключ из массива
   */
  resolveActiveKey(keys: TradeKeyWithPermissions[]) {
    // Торговые ключи
    const tradeKeys = keys.filter(k => k.canTrade === true)
    const readKeys  = keys.filter(k => k.canRead === true)
    if (tradeKeys.length == 1) {
      return of(tradeKeys[0])
    } else if (tradeKeys.length > 1) {
      return this.fetchKeysBalances(tradeKeys).pipe(
        map(balancesMap => this.findKeyWithMaxBalance(balancesMap))
      )
    } else if (readKeys.length > 0) {
      return this.fetchKeysBalances(readKeys).pipe(
        map(balancesMap => this.findKeyWithMaxBalance(balancesMap))
      )
    }
    return of(keys[0])
  }

  /**
   * Запрашивает баланс ключей
   */
  private fetchKeysBalances(keys: TradeKeyWithPermissions[]) {
    return forkJoin(keys.map(key => {
      const rest = this.rcf.createForKey(key)
      return this.balanceService.requestBalance(rest).pipe(
        catchError(() => of(0))
      )
    })).pipe(
      map(balances => {
        const map = new Map<TradeKeyWithPermissions, number>()
        keys.forEach((key, i) => map.set(key, balances[i]))
        return map
      })
    )
  }

  /**
   * Ищет ключ, у которого максимальный баланс
   */
  private findKeyWithMaxBalance(map: Map<TradeKeyWithPermissions, number>) {
    let max = 0, activeKey: TradeKeyWithPermissions | undefined
    map.forEach((balance, key) => {
      if (balance > max) {
        max = balance
        activeKey = key
      }
    })
    return activeKey ?? Array.from(map.keys())[0]
  }

  /**
   * Добавляет демо-ключи бирж, которые отсутствуют в ключах юзера
   */
  private appendDemoKeys(keys: TradeKeyWithPermissions[]) {
    const markets = keys.map(k => k.market)
    const appendKeys = this.demoKeys.filter(k => !markets.includes(k.market))
    keys.push(...cloneDeep(appendKeys))
    return keys
  }

}
